import {
  ChevronDown,
  Mic,
  MicOff,
  PhoneForwarded,
  PhoneOff,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";
import {
  CallPropertyTag,
  CallWaitingHeader,
} from "@/components/communications/call-waiting";
import { TransferPicker } from "@/components/communications/transfer-picker";
import { PersonAvatar } from "@/components/person-avatar";
import { ReservationStatusPill } from "@/components/reservations/reservation-status-pill";
import { Button } from "@/components/ui/button";
import { useApp } from "@/lib/context/use-powersync";
import { formatCallStatus } from "@/lib/format-duration";
import { useAllProperties } from "@/lib/hooks/use-all-properties";
import { useCallCounterparty } from "@/lib/hooks/use-call-counterparty";
import { ONE_SECOND_MS, useNow } from "@/lib/hooks/use-now";
import { useVoice } from "@/lib/twilio/voice-context";
import {
  cn,
  getInitialsFromDisplayName,
  getReservationStatusLabel,
} from "@/lib/utils";

/**
 * Full-screen active-call screen — what the user sees once an invite has
 * been accepted, or once they place an outgoing call. Mirrors the same
 * design vocabulary as IncomingCallView (gradient backdrop, big centred
 * avatar, name, reservation badge) so the transition from incoming →
 * active is just the controls and the pulse changing.
 *
 * The thin `<InCallOverlay />` bottom bar remains the minimised view; this
 * page is the maximised one, reached by tapping the bar.
 *
 * Auto-pops when `activeCall` clears (call ended).
 */

export interface ActiveCallViewProps {
  /** Called when the user taps the chevron-down to minimise back to the overlay. */
  onMinimise: () => void;
}

export function ActiveCallView({ onMinimise }: ActiveCallViewProps) {
  const t = useTranslations("communications.activeCallView");
  const tLabels = useTranslations("labels");
  const {
    activeCall,
    endCall,
    resumeCall,
    speakerOn,
    toggleMute,
    toggleSpeaker,
    transferCall,
  } = useVoice();
  const { currentProperty } = useApp();
  const properties = useAllProperties();
  const [showTransfer, setShowTransfer] = useState(false);
  const now = useNow(ONE_SECOND_MS, Boolean(activeCall?.startedAt));

  const { avatar, name, number, reservation, unitCode } =
    useCallCounterparty(activeCall);
  const callPropertyId =
    activeCall?.caller?.propertyId ?? currentProperty?.id ?? null;
  const callProperty = properties.find((p) => p.id === callPropertyId);
  // The coloured tag names the property instead, where it shows.
  const showsPropertyTag =
    properties.length > 1 && Boolean(activeCall?.caller?.propertyId);

  const handleShowTransfer = useCallback(() => setShowTransfer(true), []);
  const handleHideTransfer = useCallback(() => setShowTransfer(false), []);
  const callSid = activeCall?.callSid;
  const handleHangUp = useCallback(() => {
    if (callSid) {
      void endCall(callSid);
    }
  }, [callSid, endCall]);
  const handleResume = useCallback(() => {
    void resumeCall();
  }, [resumeCall]);

  const handleSelectTransferTarget = useCallback(
    (memberId: string) => {
      setShowTransfer(false);
      void transferCall(memberId);
    },
    [transferCall]
  );

  if (!activeCall) {
    return null;
  }

  // Display: the name the host dialled, or the guest or teammate the call
  // came from; the simulator's displayName as a fallback; otherwise
  // "Active call".
  // The number always renders below the heading so staff have it even
  // when they recognise the name.
  const displayName = name ?? activeCall.displayName ?? t("activeCall");
  const initials = name ? getInitialsFromDisplayName(name) : "";
  // Reservation can come from the live lookup OR from the dev simulator's
  // mock context — the latter renders the rich UI without depending on
  // whether seed data has synced down.
  const reservationStatus =
    reservation?.status ?? activeCall.dev?.reservationStatus;
  const effectiveUnitCode = unitCode ?? activeCall.dev?.unitCode ?? null;
  const statusLabel = formatCallStatus(tLabels, activeCall, now);

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        "bg-gradient-to-b from-primary/10 via-background to-background",
        "px-6 pt-[calc(env(safe-area-inset-top)+1rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]"
      )}
    >
      {/* Minimise affordance */}
      <div className="flex items-center justify-start">
        <button
          aria-label={t("minimiseCall")}
          className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-muted/50"
          onClick={onMinimise}
          type="button"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      </div>

      <CallWaitingHeader />

      {/* Avatar + name */}
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <PersonAvatar
          alt={displayName}
          avatar={avatar}
          className="shadow-xl"
          initials={initials}
          reservationStatus={reservation?.status}
          size="2xl"
        />

        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-3xl font-semibold text-foreground">
            {displayName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {[number, showsPropertyTag ? null : callProperty?.name]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <CallPropertyTag
            className="rounded-full border border-border bg-card px-3 py-1 text-sm font-medium text-foreground shadow-sm"
            propertyId={activeCall.caller?.propertyId}
          />
          <p
            className={cn(
              "text-base font-medium tabular-nums",
              activeCall.onHold || activeCall.status === "reconnecting"
                ? "text-amber-500"
                : "text-foreground"
            )}
          >
            {statusLabel}
          </p>

          {reservationStatus && effectiveUnitCode ? (
            <ReservationStatusPill
              className="mt-2"
              size="md"
              status={reservationStatus}
            >
              {t("roomStatus", {
                status: getReservationStatusLabel(tLabels, reservationStatus),
                unitCode: effectiveUnitCode,
              })}
            </ReservationStatusPill>
          ) : null}
        </div>
      </div>

      {/* Controls: a held call offers only its way back and a hang-up. */}
      {activeCall.onHold ? (
        <div className="flex items-center justify-around gap-6">
          <Button
            className="h-16 gap-2 rounded-full bg-success px-8 text-base font-semibold text-success-foreground shadow-lg transition-transform hover:opacity-90 active:scale-95"
            onClick={handleResume}
            type="button"
          >
            <Play className="size-5" />
            {t("resume")}
          </Button>
          <HangUpButton label={t("hangUp")} onClick={handleHangUp} />
        </div>
      ) : (
        <div className="flex items-center justify-around gap-6">
          <ControlButton
            icon={
              activeCall.muted ? (
                <MicOff className="size-6" />
              ) : (
                <Mic className="size-6" />
              )
            }
            label={activeCall.muted ? t("unmute") : t("mute")}
            onClick={toggleMute}
            pressed={activeCall.muted}
          />
          <ControlButton
            icon={<PhoneForwarded className="size-6" />}
            label={t("transfer")}
            onClick={handleShowTransfer}
            pressed={showTransfer}
          />
          <HangUpButton label={t("hangUp")} onClick={handleHangUp} />
          <ControlButton
            icon={
              speakerOn ? (
                <Volume2 className="size-6" />
              ) : (
                <VolumeX className="size-6" />
              )
            }
            label={speakerOn ? t("speakerOff") : t("speakerOn")}
            onClick={toggleSpeaker}
            pressed={speakerOn}
          />
        </div>
      )}

      {Boolean(showTransfer) && (
        <TransferPicker
          onClose={handleHideTransfer}
          onSelect={handleSelectTransferTarget}
          propertyId={callPropertyId}
        />
      )}
    </div>
  );
}

// Local control button (mute / speaker)

interface ControlButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  pressed: boolean;
}

function ControlButton({ label, icon, pressed, onClick }: ControlButtonProps) {
  return (
    <button
      aria-label={label}
      aria-pressed={pressed}
      className={cn(
        "flex h-14 w-14 items-center justify-center rounded-full border transition-colors",
        pressed
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-foreground hover:bg-muted"
      )}
      onClick={onClick}
      type="button"
    >
      {icon}
    </button>
  );
}

function HangUpButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={label}
      className={cn(
        "flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-full shadow-lg",
        "bg-destructive text-destructive-foreground transition-transform hover:opacity-90 active:scale-95"
      )}
      onClick={onClick}
      type="button"
    >
      <PhoneOff className="size-7" />
    </Button>
  );
}
